import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { API } from '../api';
import { TabTable, clearFiltersAction } from '../ui/EntityPage';
import EntityLink from '../ui/EntityLink';
import EmptyState from '../ui/EmptyState';
import Badge, { SeverityBadge, StatusBadge } from '../ui/Badge';
import { ticketPath } from '../ui/entityRoutes';
import { formatDate } from '../ui/format';
import usePagedQuery from '../ui/usePagedQuery';
import useUrlFilters from '../ui/useUrlFilters';

const muted = (text) => <span className="text-graphite">{text}</span>;
const dateCell = (value) => <span className="text-graphite tabular-nums">{formatDate(value) || '—'}</span>;
const ticketLinkClass = 'font-mono text-id text-signal hover:underline';

function TicketCell({ finding: f }) {
  if (!f.ticket_id) return muted('Not ticketed');
  if (String(f.ticket_mode).toLowerCase() === 'preview') {
    return (
      <Link to={ticketPath(f.ticket_id)} title={`Preview ticket, status: ${f.ticket_status}. Flow runs skip this finding until the preview is deleted.`}>
        <Badge tone="med">Held by preview #{f.ticket_id}</Badge>
      </Link>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <Link to={ticketPath(f.ticket_id)} className={ticketLinkClass}>#{f.ticket_id}</Link>
      <StatusBadge status={f.ticket_status} />
    </span>
  );
}

const COLUMNS = [
  { key: 'cve', header: 'CVE', render: (f) => (f.cve ? <EntityLink kind="cve" cve={f.cve} /> : muted('—')) },
  { key: 'severity', header: 'Severity', render: (f) => <SeverityBadge severity={f.severity} /> },
  { key: 'host', header: 'Host', render: (f) => <EntityLink kind="host" id={f.host_id} name={f.hostname} /> },
  { key: 'package', header: 'Package', render: (f) => <EntityLink kind="package" id={f.package_id} name={f.package_name} /> },
  { key: 'installed', header: 'Installed', mono: true, render: (f) => f.package_version },
  { key: 'fixed', header: 'Fixed in', render: (f) => (f.fixed_version ? <span className="font-mono text-id text-ok">{f.fixed_version}</span> : muted('No fix yet')) },
  { key: 'status', header: 'Status', render: (f) => <StatusBadge status={f.finding_status} /> },
  { key: 'first', header: 'First seen', render: (f) => dateCell(f.first_detected_at) },
  { key: 'last', header: 'Last seen', render: (f) => dateCell(f.last_detected_at) },
  { key: 'ticket', header: 'Ticket', render: (f) => <TicketCell finding={f} /> },
];

// URL keys are prefixed so they don't collide with the filters of other tabs on the same page.
const FILTER_DEFAULTS = { findings_q: '', findings_severity: '', findings_fix: '', findings_status: 'active', findings_ticketed: '' };
const isFiltered = (v) => Boolean(v.findings_q || v.findings_severity || v.findings_fix || v.findings_ticketed || v.findings_status !== 'active');

const searchPlaceholder = (hide) => {
  const parts = [!hide.includes('cve') && 'CVE', !hide.includes('package') && 'package', !hide.includes('host') && 'host'].filter(Boolean);
  return parts.join(', ');
};

const filtersFor = (hide) => [
  { key: 'findings_q', type: 'search', label: 'Search', placeholder: searchPlaceholder(hide), mono: true, width: 260 },
  {
    key: 'findings_severity',
    type: 'select',
    label: 'Severity',
    options: [
      { value: '', label: 'All' },
      { value: 'Critical', label: 'Critical' },
      { value: 'High', label: 'High' },
      { value: 'Medium', label: 'Medium' },
      { value: 'Low', label: 'Low' },
    ],
  },
  {
    key: 'findings_fix',
    type: 'select',
    label: 'Fix',
    options: [
      { value: '', label: 'All' },
      { value: 'fixed', label: 'Available' },
      { value: 'unfixed', label: 'Not available' },
    ],
  },
  {
    key: 'findings_status',
    type: 'select',
    label: 'Status',
    options: [
      { value: 'active', label: 'Active' },
      { value: 'stale', label: 'Stale' },
      { value: 'resolved', label: 'Resolved' },
      { value: 'all', label: 'All' },
    ],
  },
  {
    key: 'findings_ticketed',
    type: 'select',
    label: 'Ticket',
    options: [
      { value: '', label: 'All' },
      { value: 'yes', label: 'Ticketed' },
      { value: 'no', label: 'Not ticketed' },
    ],
  },
];

// Active findings in scope, for the tab count. scope: { hostId } | { cve } | { packageId }.
export const fetchFindingsCount = (scope) => API.GET_FINDINGS({ ...scope, limit: 1, track: false }).then((r) => r.total);

/**
 * Findings (host + package + CVE) for one entity, with the open ticket holding each one.
 * scope: { hostId } | { cve } | { packageId }. hide: column keys fixed by the page (e.g. ['host']).
 */
export default function FindingsTab({ scope, hide = [], label }) {
  const [filters, setFilters] = useUrlFilters(FILTER_DEFAULTS);
  const params = {
    ...scope,
    kw: filters.findings_q,
    severity: filters.findings_severity,
    fixed: filters.findings_fix,
    status: filters.findings_status,
    ticketed: filters.findings_ticketed,
  };
  const key = JSON.stringify(params);
  const fetchPage = useCallback((paging) => API.GET_FINDINGS({ ...params, ...paging }), [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const query = usePagedQuery(fetchPage, key);
  const columns = COLUMNS.filter((c) => !hide.includes(c.key));

  return (
    <TabTable
      label={label}
      filters={filtersFor(hide)}
      values={filters}
      onFiltersChange={setFilters}
      query={query}
      filtered={isFiltered(filters)}
      columns={columns}
      rowKey={(f) => f.finding_id}
      errorTitle="Findings could not be loaded"
      empty={<EmptyState title="No active findings">No package here has an active CVE finding.</EmptyState>}
      filteredEmpty={(
        <EmptyState title="No findings match these filters" action={clearFiltersAction(() => setFilters(FILTER_DEFAULTS))}>
          Search matches part of a CVE ID, package name or hostname. Set Status to All to include stale and resolved findings.
        </EmptyState>
      )}
    />
  );
}
